/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { PropsWithChildren, useCallback } from 'react'

import { transparentize } from 'polished'
import styled, { css } from 'styled-components'

import tgbLogoAltImage from 'src/assets/tgb-logo-alt.png'
import { useMediaScreenType } from 'src/hooks/useMediaScreenType'

import { APP_NAME } from '../../constants'
import { ModalConfig, useModalContext } from '../../contexts/ModalContext'
import { useModalGuideContext } from '../../contexts/ModalGuideContext'
import { useThemeContext } from '../../contexts/ThemeContext'
import { pxToVw } from '../../functions/units'
import Avatar from '../Avatar'
import { BaseButton } from '../Button'
import FlexBox from '../Common/FlexBox'
import Icon from '../Icon'

type Props = PropsWithChildren<{
  config: ModalConfig
  isOpen: boolean
  modalGuide?: boolean
}>

const Modal = ({ children, config, isOpen, modalGuide = false }: Props) => {
  const { colors } = useThemeContext()
  const { closeModal } = useModalContext()
  const { closeModal: closeModalGuide } = useModalGuideContext()
  const { isMobile } = useMediaScreenType()

  const handleDismiss = useCallback(() => {
    return modalGuide ? closeModalGuide() : closeModal()
  }, [closeModal, closeModalGuide, modalGuide])

  if (!isOpen) return null

  return (
    <StyledModal>
      <StyledOverlay alignItems="center" justifyContent="center">
        <StyledDialog
          className={
            config.fullscreen ? 'animate__animated animate__faster animate__slideInUp' : undefined
          }
          $fullscreen={config.fullscreen}
          $asWidgetContainer={config.asWidgetContainer}
          flexDirection="column"
        >
          {config.showCloseButton && (
            <StyledDialogCloseButton onClick={handleDismiss}>
              <FlexBox as="span" alignItems="center" justifyContent="center">
                <Icon icon={'close'} size={config.fullscreen ? 44 : 24} color={colors.gray300} />
              </FlexBox>
            </StyledDialogCloseButton>
          )}

          {config.showDialogHeader && (
            <StyledDialogHeader>
              <StyledDialogTitle columnGap={21} alignItems="center">
                <Avatar
                  image={tgbLogoAltImage}
                  circular
                  size={`${(75 / (isMobile ? 720 : 1920)) * 100}vw`}
                />

                <FlexBox flexDirection="column" rowGap={3}>
                  <small>{APP_NAME}</small>
                  <h1>{config.dialogTitle}:</h1>
                </FlexBox>
              </StyledDialogTitle>
            </StyledDialogHeader>
          )}

          <StyledDialogContent>
            {children}

            {config.showDialogFooter && (
              <StyledDialogFooter>
                <small>Upon any doubts, be sure to check the main project page.</small>
                <a href="https://thegoodblockchain.sg/" target="_blank" rel="noreferrer">
                  <FlexBox as="span" columnGap={4} alignItems="center">
                    <span>The Good Blockchain</span>
                    <Icon icon="caret-right" size={12} color={colors.primary600} />
                  </FlexBox>
                </a>
              </StyledDialogFooter>
            )}
          </StyledDialogContent>
        </StyledDialog>
      </StyledOverlay>
    </StyledModal>
  )
}

export default Modal

const StyledModal = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: 1001;
`

const StyledOverlay = styled(FlexBox)`
  background-color: ${({ theme }) => transparentize(0.33, theme.colors.baseBlack)};
  width: 100vw;
  height: 100vh;
`

const StyledDialogCloseButton = styled(BaseButton)`
  padding: 0;
  display: inline-flex;
  height: fit-content;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  position: absolute;
  top: 2.083vw;
  right: 2.083vw;
  z-index: 2;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      top: ${pxToVw(-40, 720)};
      right: ${pxToVw(40, 720)};
    `}
  `}
`

const StyledDialog = styled(FlexBox)<{
  $fullscreen: boolean
  $asWidgetContainer: boolean
}>`
  position: relative;

  ${({ theme, $fullscreen, $asWidgetContainer }) => css`
    background-color: ${$asWidgetContainer ? 'transparent' : theme.colors.bg};
    border-radius: ${$fullscreen || $asWidgetContainer ? 0 : theme.units.borderRadius.medium};
    width: ${$fullscreen ? '100vw' : '36.4583vw'};
    min-width: ${$fullscreen ? 'auto' : '420px'};
    // max-width: ${$fullscreen ? 'auto' : '700px'};
    height: ${$fullscreen ? '100vh' : $asWidgetContainer ? '85vh' : 'auto'};
    max-height: ${$fullscreen || $asWidgetContainer ? 'auto' : '85vh'};
    padding: ${$fullscreen || $asWidgetContainer ? 0 : '2.083vw 0 0'};

    ${$asWidgetContainer &&
    css`
      ${StyledDialogContent} {
        padding: 0;
        height: 100%;
        overflow: hidden;
      }
    `}

    ${$fullscreen &&
    css`
      ${StyledDialogCloseButton} {
        top: ${pxToVw(72)};
        right: ${pxToVw(72)};
      }

      ${StyledDialogContent} {
        padding-block: 0;
        padding: 1.5625vw 0 0;
      }
    `}
  `}

  ${({ theme, $fullscreen, $asWidgetContainer }) => css`
    ${theme.mediaQuery.mobile`
      width: 100vw;
      min-width: unset;
      // max-width: unset;
      position: fixed;
      bottom: 0;
      left: 0;
      padding-top: ${!$fullscreen && !$asWidgetContainer && pxToVw(40, 720)};
    `}
  `}
`

const StyledDialogHeader = styled.div`
  width: calc(100% - (2.083vw * 2));
  position: relative;
  padding-bottom: 2.083vw;
  margin-inline: auto;
  border-bottom: solid 1px ${({ theme }) => theme.colors.gray200};
  z-index: 1;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: calc(100% - (${pxToVw(40, 720)} * 2));
      padding-bottom: ${pxToVw(40, 720)};
    `}
  `}
`

const StyledDialogTitle = styled(FlexBox)`
  small {
    font-size: 1.0416vw;
    color: ${({ theme }) => theme.colors.gray300};
  }

  h1 {
    margin: 0;
    font-size: 1.5625vw;
    color: ${({ theme }) => theme.colors.gray600};
    font-weight: 500;
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      small {
        font-size: ${pxToVw(20, 720)};
      }

      h1 {
       font-size: ${pxToVw(30, 720)};
      }
    `}
  `}
`

const StyledDialogContent = styled.div`
  width: 100%;
  overflow: auto;
  padding: 1.5625vw 2.083vw 0;
  position: relative;

  /*
  ::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }
  -ms-overflow-style: none; // IE and Edge
  scrollbar-width: none; // Firefox
  */

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding: ${pxToVw(30, 720)} ${pxToVw(40, 720)} 0;
    `}
  `}
`

const StyledDialogFooter = styled.div`
  width: 100%;
  margin-top: 1.5625vw;
  padding-block: 2.083vw;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};

  small {
    display: block;
    font-size: ${pxToVw(18)};
    color: ${({ theme }) => theme.colors.gray};
    margin-bottom: ${pxToVw(12)};
  }

  a,
  a:visited {
    font-size: ${pxToVw(22)};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.primary600};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-top: ${pxToVw(30, 720)};
      padding-block: ${pxToVw(40, 720)};

      small {
        font-size: ${pxToVw(18, 720)};
        margin-bottom: ${pxToVw(12, 720)};
      }

      a, a:visited {
        font-size: ${pxToVw(22, 720)};
      }
    `}
  `}
`
